<template>
  <MsModal :modal-id="modalId" :modal-width="ModalSize.xl">
    <template v-slot:header>
      <div class="modal-title">
        <span class="ms-header-items">
          <AlarmSources :alarmSources="selectedAlarm?.alarmSources"></AlarmSources>
        </span>
        <span class="ms-header-items">
          <DateTime :timestamp="selectedAlarm?.timestamp"></DateTime>
        </span>
        <span class="ms-header-items">
          <KpCell :kp="selectedAlarm?.kp" :active="selectedAlarm?.active"></KpCell>
        </span>
      </div>
      <MsButton
        data-bs-dismiss="modal"
        aria-label="Close"
        id="close-modal"
        button-custom-style="btn-close"
      >
        <img src="@/assets/svg/cancel.svg" class="icon" />
      </MsButton>
    </template>

    <template v-slot:body>
      <div class="main-area">
        <div v-if="open">
          <h2>Alarm Details</h2>

          <div
            class="NKT-P1"
            v-if="selectedAlarm?.alarmSource?.type === AlarmSourceType.SpareParts"
          >
            Spare part is expiring on
            {{ toSparePartDate(selectedAlarm.sparePartData?.expirationDate) }}, reach out to NKT to
            order as soon as possible to reduce risk of downtime.
          </div>
          <div class="NKT-P1 mb-2" v-else>
            {{ selectedAlarm?.description }}

            <div class="mt-3" v-if="selectedAlarm?.hasRange">
              <span style="margin-right: 32px"
                >Alarm start: <DateTime :timestamp="selectedAlarm?.startTimestamp"></DateTime
              ></span>
              <span>Alarm end: <DateTime :timestamp="selectedAlarm?.timestamp"></DateTime></span>
            </div>
          </div>

          <AisDataProperties
            v-if="
              open &&
              selectedAlarm?.alarmSource?.type == AlarmSourceType.AIS &&
              selectedAlarm?.aisData
            "
            :kp="selectedAlarm?.kp"
            :data="selectedAlarm?.aisData"
            :coordinate="selectedAlarm?.coordinate"
          ></AisDataProperties>

          <SparePartsInventory
            v-if="open && selectedAlarm?.sparePartData?.sparePartGroups"
            :inventory="selectedAlarm?.sparePartData.sparePartGroups"
          ></SparePartsInventory>

          <div class="main-chart">
            <AlarmDTSChart
              :alarm="selectedAlarm"
              v-if="open && selectedAlarm?.alarmSource?.type == AlarmSourceType.DTS"
            ></AlarmDTSChart>
            <AlarmDASChart
              :alarm="selectedAlarm"
              v-if="open && selectedAlarm?.alarmSource?.type == AlarmSourceType.DAS"
            ></AlarmDASChart>
          </div>

          <div
            class="ms-related-documents-title"
            v-if="(selectedAlarm?.alarmSources?.length ?? 0) > 1"
          >
            <h2>Related Data</h2>
            <AlarmDASChart
              class="mb-6"
              :alarm="selectedAlarm"
              v-if="
                open &&
                selectedAlarm?.alarmSources?.some((x) => x.type == AlarmSourceType.DAS) &&
                selectedAlarm?.alarmSource?.type !== AlarmSourceType.DAS
              "
            ></AlarmDASChart>

            <AisDataProperties
              class="mb-6"
              v-if="
                open &&
                selectedAlarm?.alarmSources?.some((x) => x.type == AlarmSourceType.AIS) &&
                selectedAlarm?.alarmSource?.type !== AlarmSourceType.AIS &&
                selectedAlarm?.aisData
              "
              :kp="selectedAlarm?.kp"
              :data="selectedAlarm?.aisData"
            ></AisDataProperties>

            <AlarmDoBChart
              class="mb-6"
              :alarm="selectedAlarm"
              v-if="
                open &&
                selectedAlarm?.alarmSources?.some((x) => x.type == AlarmSourceType.DoB) &&
                selectedAlarm?.alarmSource?.type !== AlarmSourceType.DoB
              "
            ></AlarmDoBChart>

            <AlarmDTSChart
              :alarm="selectedAlarm"
              v-if="
                open &&
                selectedAlarm?.alarmSources?.some((x) => x.type == AlarmSourceType.DTS) &&
                selectedAlarm?.alarmSource?.type !== AlarmSourceType.DTS
              "
            ></AlarmDTSChart>
          </div>

          <div v-if="selectedAlarm?.alarmSource?.type === AlarmSourceType.AIS">
            <div class="ms-related-documents-title">
              <h5>Related Documents</h5>
            </div>
            <FilesTable :files="files"></FilesTable>
          </div>

          <div class="ms-actions">
            <MsButton
              v-if="userStore.isOwnerOrHigher"
              :disabled="sendRequestToNKT"
              :buttonType="ButtonStyles.Tertiary"
              @click="sendEvaluation"
              >Request NKT Evaluation</MsButton
            >

            <ModalButton
              v-if="userStore.isOwnerOrHigher"
              :button-type="selectedAlarm?.active ? 'Highlighted' : 'Secondary'"
              :text="selectedAlarm?.active ? 'Deactivate Alarm' : 'Reactivate Alarm'"
              @click="showDeactivateDialog = true"
            ></ModalButton>
            <InlineConfirmationDialog
              :show="showDeactivateDialog"
              :title="`Are you sure you want to ${
                selectedAlarm?.active ? 'deactivate' : 'reactivate'
              }?`"
              @confirm="confirmDeactivation"
              @cancel="cancelDeactivation"
            />
          </div>

          <div v-if="sendRequestToNKT" class="NKT-P2">
            <img class="checkmark" src="@/assets/svg/checkmark.svg" />
            We have registered your request, and you will hear from us shortly.
          </div>

          <div class="ms-logs" v-if="selectedAlarm && (selectedAlarm?.logs?.length ?? 0) > 0">
            <AlarmLogs :alarm="selectedAlarm!"></AlarmLogs>
          </div>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <MsButton
        v-if="hasPrevAlarm()"
        @click="prevAlarm"
        button-custom-style="ms-button-link-blue"
        button-font="NKT-P2"
      >
        <img :src="arrow" class="arrow-left icon mb-1" /> Previous</MsButton
      >
      <MsButton
        v-if="hasNextAlarm()"
        @click="nextAlarm"
        button-custom-style="ms-button-link-blue"
        button-font="NKT-P2"
      >
        Next
        <img :src="arrow" class="arrow-right icon mb-1" />
      </MsButton>
    </template>
  </MsModal>
</template>

<script setup lang="ts">
import arrow from '@/assets/svg/arrow.svg'
import InlineConfirmationDialog from '@/components/dialogs/InlineConfirmationDialog.vue'
import FilesTable from '@/components/documentation/FilesTable.vue'
import AisDataProperties from '@/components/properties/AisDataProperties.vue'
import AlarmLogs from '@/components/properties/AlarmLogs.vue'
import {
  Alarm,
  AlarmActivationType,
  AlarmClient,
  AlarmSourceType,
  DocumentationClient,
  File,
  FilesRequest,
  RelationType,
  UpdateActivationRequest
} from '@/server/MakeSenseApp-backend'
import { useProjectStore } from '@/stores/ProjectStore'
import { useUserStore } from '@/stores/UserStore'
import { ButtonStyles, ModalSize } from '@/utilities/enums'
import { requiredInject } from '@/utilities/requiredInject'
import moment from 'moment'
import { computed, defineProps, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AlarmSources from '../cells/AlarmSourcesCell.vue'
import DateTime from '../cells/DateTimeCell.vue'
import KpCell from '../cells/KpCell.vue'
import AlarmDASChart from '../charts/alarmCharts/AlarmDASChart.vue'
import AlarmDoBChart from '../charts/alarmCharts/AlarmDoBChart.vue'
import AlarmDTSChart from '../charts/alarmCharts/AlarmDTSChart.vue'
import ModalButton from '../general/buttons/ModalButton.vue'
import MsButton from '../general/buttons/MsButton.vue'
import MsModal from '../general/modal/MsModal.vue'
import SparePartsInventory from '../spareparts/SparePartsInventory.vue'
const modalId = 'alarmModal'

var props = defineProps({
  alarms: Array<Alarm>,
  selectedId: String,
  pushRoute: Boolean
})

var router = useRouter()

const activeAlarm = ref<Alarm | undefined>()

var open = ref(false)
var showDeactivateDialog = ref(false)
var isLoadingSubmit = ref(false)
var sendRequestToNKT = ref(false)
var files = ref<Array<File>>([])

let userStore = useUserStore()
let projectStore = useProjectStore()

var alarmClient = requiredInject<AlarmClient>('AlarmClient')

const selectedAlarm = computed(() => {
  if (activeAlarm.value !== undefined) {
    return activeAlarm.value
  }
  return props.alarms?.find((alarm) => alarm.id === props.selectedId)
})

function sendEvaluation() {
  alarmClient.requestEvaluation(projectStore.projectId)
  sendRequestToNKT.value = true
}

function hasNextAlarm() {
  const index = findIndex()
  return index !== undefined && index < props.alarms!.length - 1
}

function nextAlarm() {
  const index = findIndex()
  if (index !== undefined && index < props.alarms!.length - 1) {
    activeAlarm.value = props.alarms?.[index + 1]
    updateVisitedAlarm()
    resetState()
    refresh()
  }
}

function hasPrevAlarm() {
  const index = findIndex()
  return index !== undefined && index > 0
}

function prevAlarm() {
  const index = findIndex()
  if (index !== undefined && index > 0) {
    activeAlarm.value = props.alarms?.[index - 1]
    updateVisitedAlarm()
    resetState()
    refresh()
  }
}

function refresh() {
  open.value = false

  setTimeout(() => {
    open.value = true
  }, 1)
}

function findIndex(): number | undefined {
  return props.alarms?.findIndex((alarm) => alarm.id === activeAlarm.value?.id)
}

function toSparePartDate(date: Date | undefined) {
  return moment(date).format('YYYY-MM-DD')
}

function confirmDeactivation(comment: string) {
  let alarm = selectedAlarm.value
  if (alarm != null && alarm.projectId != null && alarm.id != null) {
    isLoadingSubmit.value = true

    var request = new UpdateActivationRequest({
      action: alarm.active ? AlarmActivationType.Deactivate : AlarmActivationType.Activate,
      comment: comment
    })

    alarmClient.updateActivation(alarm.projectId, alarm.id, request).then((updatedData) => {
      isLoadingSubmit.value = false
      alarm.active = updatedData.active
      alarm.logs = updatedData.logs
    })
  }

  showDeactivateDialog.value = false
}

function cancelDeactivation() {
  showDeactivateDialog.value = false
}

onMounted(() => {
  const modalElement = document.getElementById(modalId)
  var documentClient = requiredInject<DocumentationClient>('DocumentationClient')

  if (modalElement) {
    modalElement.addEventListener('show.bs.modal', handleShow)
    modalElement.addEventListener('shown.bs.modal', handleShown)
    modalElement.addEventListener('hide.bs.modal', handleHide)
  }

  watch(
    () => [projectStore.selectedProject],
    () => {
      documentClient
        .getFiles(
          new FilesRequest({
            relatedProjects: [projectStore.projectId],
            relationType: RelationType.AISMonitoringReport
          })
        )
        .then((data) => {
          files.value = data
        })
    }
  ),
    { immediate: true }
})

onUnmounted(() => {
  const modalElement = document.getElementById(modalId)
  if (modalElement) {
    modalElement.removeEventListener('show.bs.modal', handleShow)
    modalElement.removeEventListener('shown.bs.modal', handleShown)
    modalElement.removeEventListener('hide.bs.modal', handleHide)
  }
})

const handleShow = () => {
  open.value = true
  isLoadingSubmit.value = false
}

const handleHide = () => {
  if (props.pushRoute) {
    router.replace({ name: 'alarms' })
  }

  open.value = false
  activeAlarm.value = undefined
  resetState()
}

const handleShown = function () {
  updateVisitedAlarm()
  activeAlarm.value = props.alarms?.find((alarm) => alarm.id === props.selectedId)
}

function updateVisitedAlarm() {
  var alarm = selectedAlarm.value
  if (alarm && alarm.projectId && alarm.id) {
    alarmClient.visitedAlarm(alarm.projectId, alarm.id)
    alarm.visited = true

    if (props.pushRoute) {
      router.replace({ name: 'alarms', params: { id: alarm.id } })
    }
  }
}

function resetState() {
  showDeactivateDialog.value = false
  isLoadingSubmit.value = false
  sendRequestToNKT.value = false
}
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.modal-body {
  height: calc(100vh - 205px);
  overflow-y: auto;
}

.ms-header-items {
  margin-right: 24px;
}

.main-chart {
  margin-top: 32px;
}

.main-area {
  margin-bottom: 12px;
}

.ms-action-btn {
  margin-right: 12px;
}

.ms-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  position: relative;
}

.ms-logs {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 10px 0;
}

.deactivate-dialog {
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 256px;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 10;

  .btn {
    .ms-check-icon {
      color: green;
      width: 24px;
    }

    .ms-x-icon {
      color: red;
      width: 24px;
    }
  }
}

.checkmark {
  margin-bottom: 3px;
}

.dialog-title {
  margin-bottom: 8px;
}

.comment-area {
  border: 1px solid color.$color-nordic-blue-base;
  resize: none;
}

.btn-text {
  background: none;
  border: none;
  color: inherit;
  font-size: 14px;
  cursor: pointer;
}

.btn-text i {
  margin-left: 5px;
}

.arrow-left {
  transform: rotate(90deg);
}

.arrow-right {
  transform: rotate(270deg);
}
</style>
